import { type CreateFacilityDuplicateCheckValues } from "./createFacilityForm";
import { type FacilitiesSearchQueryInput, type FacilitySearchResult } from "../queries";

export function normalizeDuplicateText(value: string | null | undefined): string {
  return (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function duplicateDigits(value: string | null | undefined): string {
  return (value || "").replace(/\D/g, "");
}

export function getDuplicateAddressSearchValue(values: CreateFacilityDuplicateCheckValues): string {
  const mainAddress = values.mainAddress;
  return typeof mainAddress === "object" && mainAddress?.address1 ? mainAddress.address1 : "";
}

export function buildDuplicateSearchTerms(values: CreateFacilityDuplicateCheckValues): string[] {
  return Array.from(
    new Set([values.name || "", values.phone || "", getDuplicateAddressSearchValue(values)].filter(Boolean))
  );
}

export function buildDuplicateSearchInputs(values: CreateFacilityDuplicateCheckValues): FacilitiesSearchQueryInput[] {
  const searchInputs = buildDuplicateSearchTerms(values).map((search) => ({
    search,
    excludeArchived: true
  }));

  if (!values.sectionId) {
    return searchInputs;
  }

  return [
    ...searchInputs,
    {
      search: "",
      excludeArchived: true,
      sectionIds: [values.sectionId],
      ...(values.chapterId && { chapterIds: [values.chapterId] })
    }
  ];
}

export function matchesDuplicateFacility(candidate: FacilitySearchResult, values: CreateFacilityDuplicateCheckValues) {
  const importedName = normalizeDuplicateText(values.name || "");
  const candidateName = normalizeDuplicateText(candidate.name);
  const nameMatches =
    !!importedName &&
    (candidateName === importedName || candidateName.includes(importedName) || importedName.includes(candidateName));

  const importedPhone = duplicateDigits(values.phone || "");
  const candidatePhone = duplicateDigits(candidate.phone?.phoneNumber);
  const phoneMatches =
    importedPhone.length >= 7 &&
    candidatePhone.length >= 7 &&
    (candidatePhone.includes(importedPhone) || importedPhone.includes(candidatePhone));

  const importedAddress = values.mainAddress;
  const candidateAddress = candidate.mainAddress;
  const addressMatches =
    typeof importedAddress === "object" &&
    !!importedAddress?.address1 &&
    !!candidateAddress?.address1 &&
    normalizeDuplicateText(importedAddress.address1) === normalizeDuplicateText(candidateAddress.address1) &&
    (!importedAddress.postalCode || importedAddress.postalCode === candidateAddress.postalCode);

  return nameMatches || phoneMatches || addressMatches;
}
